/* eslint-disable */
import { useEffect, useRef } from 'react';
export const DEFAULT_REFRESH_INTERVAL_MS = 30000;
export const DEFAULT_IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export interface ConfigItem {
  key: string;
  value: string;
}

export function parseRefreshInterval(
  config: ConfigItem[] | undefined
): number | null {
  const enabledItem = config?.find((c) => c.key === 'AUTO_REFRESH_ENABLED');
  if (enabledItem && enabledItem.value === '0') return null;

  const item = config?.find((c) => c.key === 'AUTO_REFRESH_INTERVAL');
  if (!item) return DEFAULT_REFRESH_INTERVAL_MS;
  const parsed = parseInt(item.value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_REFRESH_INTERVAL_MS;
  return parsed * 1000;
}

export function parseIdleTimeout(
  config: ConfigItem[] | undefined
): number {
  const item = config?.find((c) => c.key === 'AUTO_REFRESH_IDLE_TIMEOUT');
  if (!item) return DEFAULT_IDLE_TIMEOUT_MS;
  const parsed = parseInt(item.value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_IDLE_TIMEOUT_MS;
  return parsed * 1000;
}

interface UseAutoRefreshOptions {
  intervalMs: number | null;
  idleTimeoutMs?: number;
  isDialogOpen: boolean;
  onRefresh: () => void;
}

export function useAutoRefresh({ intervalMs, idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS, isDialogOpen, onRefresh }: UseAutoRefreshOptions): void {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isIdleRef = useRef(false);

  useEffect(() => {
    // Feature disabled — do nothing
    if (intervalMs === null) return;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    isIdleRef.current = false;

    // Don't start if dialog open
    if (isDialogOpen) return;

    // Start the refresh interval
    const startInterval = () => {
      if (!intervalRef.current && intervalMs !== null && !isDialogOpen) {
        intervalRef.current = setInterval(onRefresh, intervalMs);
      }
    };

    // Stop the refresh interval
    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Reset idle timer — called on user activity
    const resetIdleTimer = () => {
      // If user was idle, resume refresh
      if (isIdleRef.current) {
        isIdleRef.current = false;
        startInterval();
      }
      // Reset the idle countdown
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      idleTimerRef.current = setTimeout(() => {
        isIdleRef.current = true;
        stopInterval();
      }, idleTimeoutMs);
    };

    // Start interval and idle timer
    startInterval();
    resetIdleTimer();

    // Listen for user activity (click and keydown indicate real interaction)
    document.addEventListener('click', resetIdleTimer);
    document.addEventListener('keydown', resetIdleTimer);

    return () => {
      document.removeEventListener('click', resetIdleTimer);
      document.removeEventListener('keydown', resetIdleTimer);
      stopInterval();
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [intervalMs, idleTimeoutMs, isDialogOpen, onRefresh]);
}
